import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { PartnerInvoice } from 'src/interfaces/transactionsReview'
import { formatDateOnly, parseIsoDate } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'

interface Props {
  invoice: PartnerInvoice
  isSelected: boolean
  onSelect(invoiceId: string): void
}

/** A radio card. `role="radio"` + keyboard handler — it is a clickable div. */
const InvoiceOptionCardComponent = ({ invoice, isSelected, onSelect }: Props) => {
  const remaining = invoice.amount - invoice.paidAmount
  const label = invoice.invoiceId ?? invoice.partnerComposedInvoiceId

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(invoice.id)
    }
  }

  return (
    <div
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={() => {
        onSelect(invoice.id)
      }}
      onKeyDown={handleKeyDown}
      data-testid={`assign-invoice-option-${invoice.id}`}
      css={[
        tw`flex cursor-pointer flex-col gap-1 rounded-lg border border-solid p-3`,
        isSelected ? tw`border-primary-500 bg-primary-100` : tw`border-shades-100 bg-shades-0`,
      ]}
    >
      <div css={tw`flex flex-row items-center justify-between gap-2`}>
        <Typography variant="h6">{label}</Typography>
        <Typography variant="body2" css={tw`text-xs text-shades-500`}>
          due {formatDateOnly(parseIsoDate(invoice.dueDate))}
        </Typography>
      </div>
      {invoice.description ? (
        <Typography variant="body2" css={tw`text-xs text-shades-500`}>
          {invoice.description}
        </Typography>
      ) : null}
      <Typography variant="body2">
        Total {formatCentsAsUsd(invoice.amount)} · Paid {formatCentsAsUsd(invoice.paidAmount)} ·
        Remaining <b>{formatCentsAsUsd(remaining)}</b>
      </Typography>
    </div>
  )
}

export const InvoiceOptionCard = memo(InvoiceOptionCardComponent)
